/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { UnpaidInvoicePayment } from '@futrli-sdk/predict';
import get from 'lodash/get';
import { penceToAmount } from '../../../core/formatters';
import { getDateWithStartOfDay } from '../../../utils/dates';

import { Data } from '../../../utils/table-filters/filter-table-types';
import { SplitPayment } from './edit-payments';

const initialPayments = [
  {
    amount: undefined,
    date: undefined,
    percent: undefined
  },
  {
    amount: undefined,
    date: undefined,
    percent: undefined
  },
  {
    amount: undefined,
    date: undefined,
    percent: undefined
  }
];

/**
 * Populating initial form data. If payment is defined it will use payment's data for
 * filling form. If payment is not defined it will put row amount in first form field and add
 * to empty rows in form to simulate split payment situation
 * @param payment Unpaid payment that is being update
 * @param data Current row data
 * @returns {SplitPayment[]}
 */
export const getInitialData = (payment: UnpaidInvoicePayment | null, data?: Data, forecastFromDate?: string | null) => {
  return payment
    ? [
        ...payment.items.map(item => ({
          amount: String(penceToAmount(item.amount)),
          percent: String(item.percent),
          date: getDateWithStartOfDay(new Date(item.date))
        }))
      ]
    : [
        {
          amount: String(penceToAmount(get(data, 'total_payable') || 0)),
          date: forecastFromDate
            ? getDateWithStartOfDay(new Date(forecastFromDate))
            : getDateWithStartOfDay(new Date()),
          percent: '1'
        },
        ...initialPayments.slice(1)
      ];
};

export const roundTwoDecimals = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Returns true if dates are sorted in chronological order, otherwise false
 * @param dates list of date objects
 * @returns {boolean}
 */
export const checkDatesOrder = (dates: Date[]) => {
  return dates.every((date, index, datesArray) => {
    return !index || datesArray[index - 1].getTime() < date.getTime();
  });
};

/**
 * Checks if form is in valid situation.
 *  - If amount is entered, date also needs to be entered
 *  - If date is entered, amount also needs to be entered
 *  - we can't have empty payments in middle of two defined payments
 * @param data split payment objects
 * @returns {boolean}
 */
export const checkFormDataCorrectlyFilled = (data: SplitPayment[]) => {
  return data.every((data, index, dataArray) => {
    if (index > 0 && index < dataArray.length - 1) {
      if (Number(dataArray[index - 1].amount) && Number(dataArray[index + 1].amount) && !Number(data.amount)) {
        return false;
      }
      if (dataArray[index - 1].date && dataArray[index + 1].date && !data.date) {
        return false;
      }
    }

    if ((Number(data.amount) && !data.date) || (!Number(data.amount) && data.date)) {
      return false;
    }

    return true;
  });
};

/**
 * Calculates entered sum of amounts and returns difference between
 * initial payment value and calculated sum of entered values
 * @param data Array of split payments objects
 * @param initialPayment initial payment value
 * @returns {number}
 */
export const getDiffToInitialPayment = (data: SplitPayment[], initialPayment: number) => {
  const valuesSum = data.reduce((acc, curr) => {
    if (typeof curr.amount === 'string') {
      return acc + Number(curr.amount);
    }
    return acc;
  }, 0);

  // Round the comparison to 2dp to match the field inputs and avoid
  // floating-point errors
  return Number.parseFloat((initialPayment - valuesSum).toFixed(2));
};

/**
 * Formats date object to "YYYY-MM-DD"
 * @param date date object
 * @returns {string}
 */
export const formatDate = (date: Date): string => {
  return `${date?.getFullYear()}-${
    Number(date?.getMonth()) + 1 < 10 ? `0${Number(date?.getMonth()) + 1}` : Number(date?.getMonth()) + 1
  }-${Number(date?.getDate()) < 10 ? `0${date?.getDate()}` : date?.getDate()}`;
};
